// Follow-the-bottom for a scrolling transcript.
//
// Two surfaces render a conversation that grows while you read it — the dashboard's
// `DashboardThread` and the project editor's `ChatPanel` — and both were wrong in
// opposite directions. The dashboard had NO scroll logic at all, so a server run
// (which appends a message per round on a 2 s poll, with no token stream) left the
// newest reply below the fold until you dragged the bar yourself. ChatPanel had an
// unconditional `scrollIntoView` on every `messages` change, which yanked you back
// down the moment you scrolled up to re-read something.
//
// The rule both wanted: follow the bottom by default, BREAK the follow when the user
// scrolls away from it, and RE-ARM it when they come back. Once broken, new content
// must not move the viewport at all.
//
// `scrollTop = scrollHeight`, NEVER `scrollIntoView` on a trailing sentinel.
// `.dash-thread-messages` sits inside `.dashboard-centre`, which is ALSO
// `overflow-y: auto` — and `scrollIntoView` walks up and scrolls every ancestor
// scroller it finds, so the page itself would lurch. Direct assignment touches the
// one element. (It is instant because no stylesheet sets `scroll-behavior: smooth`;
// setting one would silently turn every append into an animation.)
//
// The follow flag is a REF, not state. Nothing renders from it, and a state write
// per scroll event would re-render the whole transcript while the user drags. A
// future "↓ Jump to latest" button is what would need a state mirror.

import { useCallback, useLayoutEffect, useRef } from 'react';

/** How close to the bottom still counts as "at the bottom", in px. */
export const BOTTOM_EPSILON = 24;

/**
 * Is this scroller at (or within `epsilon` of) its bottom?
 *
 * Reads only `scrollTop`/`scrollHeight`/`clientHeight`, so it can be exercised
 * against a plain object — jsdom has no layout and reports all three as 0.
 * A missing element counts as at-bottom: nothing has been scrolled away from yet,
 * so the caller should keep following.
 */
export function isAtBottom(el, epsilon = BOTTOM_EPSILON) {
  if (!el) return true;
  return el.scrollHeight - el.scrollTop - el.clientHeight <= epsilon;
}

/**
 * Keep a scroller pinned to its bottom until the user scrolls up.
 *
 * @param deps     values that make the content taller (the message list, plus
 *                 anything else rendered below it — an indicator that appears and
 *                 disappears changes the height too).
 * @param resetKey re-arm following when this changes. Pass the conversation's
 *                 identity: neither consumer is remounted between conversations, so
 *                 without it a follow broken in one is inherited by the next.
 * @returns `{scrollerRef, onScroll}` — spread onto the scrolling element.
 */
export default function useStickToBottom(deps, resetKey) {
  const scrollerRef = useRef(null);
  const followingRef = useRef(true);

  const onScroll = useCallback(() => {
    followingRef.current = isAtBottom(scrollerRef.current);
  }, []);

  // Declared BEFORE the scroll effect below so that when `resetKey` changes, both
  // fire in the same commit and following is re-armed before the scroll is decided.
  useLayoutEffect(() => {
    followingRef.current = true;
  }, [resetKey]);

  // `useLayoutEffect` so the jump lands before paint — with `useEffect` a new
  // message is briefly visible above the fold and then snaps away.
  //
  // Needs no "this scroll was ours" flag: the assignment fires `onScroll`, which
  // re-measures and finds us at the bottom, so following simply stays on.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (el && followingRef.current) el.scrollTop = el.scrollHeight;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { scrollerRef, onScroll };
}
